import { range } from '../control-flow/range';

interface Box {
    position: {x: number, y: number };
    width: number;
    height: number;
}

interface Divider extends Box {
    thickness: number;
};

/**
 * This little adapter helps the StackLayoutHelper to deal with dividers
 * correctly using their thickness rather than width and height.
 */
export class DividerBox implements Box {
    divider: Divider;
    constructor(divider: Divider) {
        this.divider = divider;
    }
    get position() {
        return this.divider.position;
    }

    set position(value) {
        this.divider.position.x = value.x;
        this.divider.position.y = value.y;
    }

    get width() {
        return this.divider.thickness || 0.75;
    }

    set width(value) {
        this.divider.width = value;
    }

    get height() {
        return this.divider.thickness || 0.75;
    }

    set height(value) {
        this.divider.height = value;
    }
}

/**
 * The StackLayoutHelper
 */
export default class StackLayoutHelper {

    width: number = 1;
    height: number = 1;
    orientation: "width" | "height" = "width";
    alignment: "start" | "end" = "start";

    boxes: Box[] = [];

    constructor(
        width?: number,
        height?: number,
        orientation?: "width" | "height",
        alignment?: "start" | "end",
        boxes?: Box[])
    {
        if(width) this.width = width;
        if(height) this.height = height;
        if(orientation) this.orientation = orientation;
        if(alignment) this.alignment = alignment;
        if(boxes) this.boxes = boxes;
    }

    /**
     * This is always opposite to our orientation so we can use it to access
     * the *other* dimension. This is useful because the boxes in a stack
     * always inherit their transverse dimension from their parent.
     */
    get transverse(): "width" | "height" {
        if(this.orientation == "width") {
            return "height";
        } else {
            return "width";
        }
    }

    /**
     * If we're width-oriented (this.orientation == 'width'), then we're
     * stacking in the X direciton. Otherwise we're stacking in the Y
     * direction.
     */
    get component(): "x" | "y" {
        if(this.orientation == "height") {
            return "y";
        } else {
            return "x";
        }
    }

    get otherComponent(): "x" | "y" {
        if(this.orientation == "height") {
            return "x";
        } else {
            return "y";
        }
    }

    layout(): boolean {
        if(this.boxes.length == 0) return;

        let changed: boolean;

        // The default is start, so anything but 'end' explicitly should give
        // us a start layout.
        if(this.alignment == 'end') {
            changed = this.layoutEnd();
        }
        else {
            changed = this.layoutStart();
        }

        for(let box of this.boxes) {
            // The transverse property is the width/height that is opposite to
            // our actual orientation
            box[this.transverse] = this[this.transverse];
        }

        return changed;
    }

    /**
     * Takes care of the box offsets when the layout is aligned toward the
     * start. This is called by layout() and shouldn't be needed elsewhere.
     */
    private layoutStart(): boolean {
        const dim = this.component;
        const otherDim = this.otherComponent;

        let offset = 0;
        let changed = false;

        for(let i of range(0, this.boxes.length - 1)) {
            const box = this.boxes[i];
            if(box.position[dim] != offset) {
                box.position[dim] = offset;
                box.position[otherDim] = 0;
                changed = true;
            }
            offset += box[this.orientation];
        }
        let final = this.boxes[this.boxes.length - 1];
        if(final.position[dim] != offset) {
            final.position[dim] = offset;
            final.position[otherDim] = 0;
            changed = true;
        }
        final[this.orientation] = this[this.orientation] - offset;

        return changed;
    }

    /**
     * Takes care of the box offsets when the layout is aligned toward the
     * end. This is called by layout() and shouldn't be needed elsewhere.
     */
    private layoutEnd(): boolean {
        const dim = this.component;
        const otherDim = this.otherComponent;

        let offset = this[this.orientation];
        let total = 0;

        let changed = false;

        for(let i of range(this.boxes.length - 1, 0)) {
            const box = this.boxes[i];
            offset -= box[this.orientation];
            if(box.position[dim] != offset) {
                box.position[dim] = offset;
                box.position[otherDim] = 0;
                changed = true;
            }
            total += box[this.orientation];
        }

        let start = this.boxes[0];
        if(start.position[dim] != 0) {
            start.position[dim] = 0;
            start.position[otherDim] = 0;
            changed = true;
        }
        start[this.orientation] = this[this.orientation] - total;

        return changed;
    }
}
